import {
  DiscussionContext,
  DiscussionContextType,
  DiscussionType,
  QUERY_KEYS,
} from "@cnx/common";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import MessageService from "services/MessageService";
import { useChatPanelStore } from "stores/chatPanel";
import { getDraftTitle, getQueryKeysForDiscussionContext } from "utils/chat";

interface Params {
  discussionId?: string | null;
  contextId?: string | null;
  context?: DiscussionContextType | null;
  discussion?: {
    title?: string;
    stopActivityKind?: string | null;
    discussionType?: DiscussionType;
    context?: DiscussionContextType;
  } | null;
  discussionContext?: DiscussionContext;
}

export const useChatPanelInner = ({
  discussionId,
  contextId,
  context,
  discussion,
  discussionContext,
}: Params) => {
  const queryClient = useQueryClient();
  const setPanelDiscussionId = useChatPanelStore(
    store => store.setPanelDiscussionId
  );

  const title = discussion?.title ?? getDraftTitle(discussionContext);
  const stopActivityKind =
    discussion?.stopActivityKind ?? discussionContext?.activityKind;
  const discussionType = discussion?.discussionType ?? DiscussionType.CONTEXT;
  const showHeader = !!discussion || (!!context && !!contextId);

  const handleDiscussionCreated = useCallback(
    async (newDiscussionId: string) => {
      await queryClient.fetchQuery({
        queryKey: [QUERY_KEYS.MESSAGES, newDiscussionId],
        queryFn: () =>
          MessageService.fetchMessagesByDiscussionId({
            discussionId: newDiscussionId,
          }),
      });

      setPanelDiscussionId(newDiscussionId, contextId ?? null, context ?? null);

      const contextQueryKeys = context
        ? getQueryKeysForDiscussionContext(context)
        : undefined;
      await Promise.all([
        ...(contextQueryKeys ?? []).map(qk =>
          queryClient.invalidateQueries({ queryKey: qk })
        ),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.DISCUSSION_CONTEXT],
        }),
      ]);
    },
    [queryClient, setPanelDiscussionId, contextId, context]
  );

  return {
    title,
    stopActivityKind,
    discussionType,
    showHeader,
    handleDiscussionCreated,
  };
};
